// ============================================================================
// Cattle Log attachment URL adapter
// ============================================================================
//
// Cattle Log entries carry comment-attachment-shaped JSON, but their server
// contract is narrower than a general record comment: every object lives in
// comment-photos under cattle.log/cattle-log/. This adapter proves that
// narrower selector before handing rows to the shared attachment Query owner.
// It deliberately does not create a second signer, Query resource, route
// obligation, or boot claim. The shared owner remains responsible for the
// ten-minute TTL, pre-expiry refresh, abort fencing, and cross-observer dedupe.
// ============================================================================

import {useMemo} from 'react';

import type {AuthState} from '../contexts/AuthContext.js';
import {COMMENT_ATTACHMENT_BUCKET} from '../lib/commentAttachments.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {
  commentAttachmentUrlFor,
  useRecordCollaborationCommentAttachmentUrls,
} from '../shared/recordCollaborationAttachmentData.js';
import type {
  CommentAttachmentUrlMap,
  CommentAttachmentUrlProjection,
  CommentAttachmentUrlRow,
} from '../shared/recordCollaborationAttachmentData.js';

export const CATTLE_LOG_ATTACHMENT_BUCKET = COMMENT_ATTACHMENT_BUCKET;
export const CATTLE_LOG_ATTACHMENT_PATH_PREFIX = 'cattle.log/cattle-log/';

export type CattleLogAttachmentUrlInput = {
  readonly path?: unknown;
  // Persisted cattle-log JSON is untrusted at this boundary. This member is
  // intentionally ignored rather than passed to the shared two-bucket
  // normalizer: cattle-log objects are always comment-photos objects.
  readonly bucket?: unknown;
};

export type CattleLogAttachmentEntry = {
  readonly attachments?: unknown;
};

export type CattleLogAttachmentUrlProjection = CommentAttachmentUrlProjection;

export type NormalizedCattleLogAttachmentUrlInput = {
  readonly cacheKey: string;
  readonly bucket: typeof CATTLE_LOG_ATTACHMENT_BUCKET;
  readonly path: string;
};

const EMPTY_CATTLE_LOG_ATTACHMENT_URL: CommentAttachmentUrlProjection = Object.freeze({
  url: null,
  loading: false,
  error: null,
});

function isCattleLogAttachmentPath(path: string): boolean {
  if (!path.startsWith(CATTLE_LOG_ATTACHMENT_PATH_PREFIX)) return false;
  const suffix = path.slice(CATTLE_LOG_ATTACHMENT_PATH_PREFIX.length);
  return suffix.split('/').every((segment) => Boolean(segment) && segment !== '.' && segment !== '..');
}

/**
 * Cattle Log's submit/edit RPC accepts only this exact object namespace. Keep
 * the reader no broader than the writer: a forged bucket is ignored and a
 * forged path fails closed into the thumbnail's existing unavailable fallback.
 */
export function normalizeCattleLogAttachmentUrlInput(
  attachment: CattleLogAttachmentUrlInput | null | undefined,
): NormalizedCattleLogAttachmentUrlInput | null {
  const path = typeof attachment?.path === 'string' ? attachment.path : '';
  if (!path || !isCattleLogAttachmentPath(path)) return null;
  return {
    cacheKey: `${CATTLE_LOG_ATTACHMENT_BUCKET}:${path}`,
    bucket: CATTLE_LOG_ATTACHMENT_BUCKET,
    path,
  };
}

/**
 * Convert the visible Cattle Log rows into the shared owner's input contract.
 * Invalid metadata is omitted before it can select any object. Each accepted
 * row carries the fixed bucket explicitly, so the shared normalizer sees the
 * same cache identity the Cattle Log presenter later asks for.
 */
export function cattleLogAttachmentRows(
  entries: readonly CattleLogAttachmentEntry[] | null | undefined,
): readonly CommentAttachmentUrlRow[] {
  return (entries || []).map((entry) => {
    const attachments = Array.isArray(entry.attachments)
      ? entry.attachments.flatMap((attachment) => {
          const normalized = normalizeCattleLogAttachmentUrlInput(
            attachment && typeof attachment === 'object' ? (attachment as CattleLogAttachmentUrlInput) : undefined,
          );
          return normalized ? [{path: normalized.path, bucket: normalized.bucket}] : [];
        })
      : [];
    return {attachments};
  });
}

/**
 * Cattle Log's one mount-level bridge to the shared comment-attachment Query
 * owner. It intentionally does not own a physical storage call itself.
 */
export function useCattleLogAttachmentUrls({
  sb,
  authState,
  entries,
}: {
  sb: SupabaseClient;
  authState: AuthState;
  entries: readonly CattleLogAttachmentEntry[] | null | undefined;
}): CommentAttachmentUrlMap {
  const rows = useMemo(() => cattleLogAttachmentRows(entries), [entries]);
  return useRecordCollaborationCommentAttachmentUrls({sb, authState, comments: rows});
}

/** The Cattle Log presenter's fixed-scope fallback/projection lookup. */
export function cattleLogAttachmentUrlFor(
  urls: CommentAttachmentUrlMap,
  attachment: CattleLogAttachmentUrlInput | null | undefined,
): CommentAttachmentUrlProjection {
  const normalized = normalizeCattleLogAttachmentUrlInput(attachment);
  return normalized
    ? commentAttachmentUrlFor(urls, {path: normalized.path, bucket: normalized.bucket})
    : EMPTY_CATTLE_LOG_ATTACHMENT_URL;
}
